import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { getCachedSignedUrl, useStore } from '@mcn-platform/models';
import SignedVideoPlayer from '../aws/SignedVideoPlayer';
import VideoPlayer from '../CustomizedPlayer/VideoPlayer';
/**
 * AssetVideoPlayer - Video player component that handles Asset-based video playback
 *
 * This component automatically determines the best playback method based on the asset
 * configuration and environment:
 *
 * **HLS Mode (preferred):**
 * - Used when `asset.useHls` is true and an item context is provided
 * - Fetches an authenticated HLS stream URL via `/play/` endpoint
 * - Automatically falls back to signed URL mode if HLS fails
 * - Not used on localhost for development convenience
 *
 * **Signed URL Mode (fallback):**
 * - Generates pre-signed AWS S3 URLs for direct video access
 * - Used when HLS is unavailable, disabled, or errors
 * - Handles URL signing and renewal automatically
 *
 * The component wraps either VideoPlayer (for HLS) or SignedVideoPlayer (for S3),
 * providing a consistent interface regardless of the underlying playback method.
 *
 * @param asset - The video asset to play
 * @param assetPoster - Optional poster/thumbnail asset
 * @param item - Optional item context (required for HLS mode) - used for authorization
 * @param ...baseProps - Standard video player properties (controls, seeking, callbacks, etc.)
 *
 * @example
 * ```tsx
 * <AssetVideoPlayer
 *   asset={videoAsset}
 *   assetPoster={thumbnailAsset}
 *   item={courseItem}
 *   showControls={true}
 *   disableSeekAhead={true}
 *   onProgress={(current, max) => saveProgress(current)}
 *   onEnd={() => markComplete()}
 * />
 * ```
 */
const AssetVideoPlayer = ({ asset, assetPoster, showControls, seekPosition, initialMaxPosition, seekTrigger, onProgress, onEnd, onPause, onDuration, handleNextPrevious, disableSeekAhead, aspectRatio, playerStyle, item, }) => {
    const [hlsVideoUrl, setHlsVideoUrl] = React.useState();
    const [hlsPosterUrl, setHlsPosterUrl] = React.useState();
    const [disableHls, setDisableHls] = React.useState(false);
    const store = useStore();
    const useHls = !disableHls
        && (typeof window !== 'undefined')
        && window?.location?.hostname !== 'localhost'
        && asset?.useHls
        && !!item;
    const onHlsPlayerError = (error) => {
        console.log('onHlsError', error);
        if (error === 'hlsError') {
            setDisableHls(true);
        }
    };
    useEffect(() => {
        const authHlsVideo = async () => {
            await store.load('asset', assetPoster?.id);
            await store.load('repository', assetPoster?.repository?.id);
            const posterUrl = await getCachedSignedUrl(assetPoster?.repository?.bucket, `${assetPoster?.path}/original`);
            setHlsPosterUrl(posterUrl);
            const authUrl = `/play/${store.currentOrganization.id}/${store.currentPortal.id}/course/${item?.key}/${asset?.key}`;
            console.log('authUrl', authUrl);
            try {
                const videoResponse = await fetch(authUrl);
                // console.log('videoResponse', videoResponse);
                const videoResponseJson = await videoResponse.json();
                // console.log('videoResponseJson', videoResponseJson);
                // console.log('video response', videoResponse);
                setHlsVideoUrl(videoResponseJson.path);
            }
            catch (e) {
                console.log('error', e);
                setDisableHls(true);
            }
        };
        if (useHls) {
            authHlsVideo();
        }
    }, [asset, assetPoster, item, item?.syncState, useHls, store]);
    if (useHls) {
        // if (!hlsVideoUrl) return null;
        // we're ready, show the video player
        return (_jsx(VideoPlayer, { url: hlsVideoUrl || '', posterUrl: hlsPosterUrl, onProgress: onProgress, onEnd: onEnd, onError: onHlsPlayerError, onPause: onPause, onDuration: onDuration, showControls: showControls, seekPosition: seekPosition, initialMaxPosition: initialMaxPosition, seekTrigger: seekTrigger, handleNextPrevious: handleNextPrevious, aspectRatio: aspectRatio, playerStyle: playerStyle, disableSeekAhead: disableSeekAhead }));
    }
    return (_jsx(SignedVideoPlayer, { bucket: asset?.repository?.bucket, path: `${asset?.path}/original`, bucketPoster: assetPoster?.repository?.bucket, pathPoster: assetPoster?.path ? `${assetPoster.path}/original` : undefined, onProgress: onProgress, onEnd: onEnd, onPause: onPause, onDuration: onDuration, showControls: showControls, seekPosition: seekPosition, initialMaxPosition: initialMaxPosition, seekTrigger: seekTrigger, handleNextPrevious: handleNextPrevious, aspectRatio: aspectRatio, playerStyle: playerStyle, disableSeekAhead: disableSeekAhead }));
};
AssetVideoPlayer.defaultProps = {
    assetPoster: undefined,
    item: undefined,
};
export default observer(AssetVideoPlayer);
//# sourceMappingURL=AssetVideoPlayer.js.map